/** Позволяет создавать неймспейсы для любых функций */
export function withNamespace<T extends AnyFunction>(
  /** Оригинальная функция к которой будет добавлен метод `namespace` */
  fn: T,
  /** Функция которая будет вызвана перед выполнением оригинальной функции */
  apply: (
    /** Склеенное с помощью `delimiter` значение */
    namespace: string,
    /** Оригинальные параметры переданные при вызове `fn` */
    ...args: Parameters<T>
  ) => Parameters<T>,
  /** Разделитель с помощью которого будут соединены неймспейсы */
  delimiter = ':'
) {
  function createNamespace(this: unknown, namespace: string) {
    if (!this || typeof this !== 'function') throw new Error(`invalid "this" value "${this}"`)
    if (!namespace || typeof namespace !== 'string') throw new Error(`invalid "namespace" value "${namespace}"`)

    const key = '__namespace__' as const
    const parentNamespace = key in this && typeof this[key] === 'string' && this[key]
    const namespaceValue = [parentNamespace, namespace].filter(Boolean).join(delimiter)

    const attachNamespaceValue = (val: object) => Object.assign(val, {[key]: namespaceValue})
    const applyNamespace = (...args: Parameters<T>) => fn(...apply(namespaceValue, ...args))

    return Object.assign(attachNamespaceValue(applyNamespace), {namespace: createNamespace})
  }

  return Object.assign(fn, {namespace: createNamespace}) as unknown as Namespaced<T>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (...args: any[]) => any
type NamespaceFn<T> = (
  /** Название */
  namespace: string
) => T
type Namespaced<T> = T & {
  /** Создает новый неймспейс */
  namespace: NamespaceFn<Namespaced<T>>
}
